'use client';

/**
 * 我的 Apps 页（FF AI Workspace 唯一暴露给 internal-app-platform 的前端界面）
 *
 * 职责：onboarding（颁发/复制/重新生成/撤销 token）+ 自助查看 app 列表
 *
 * 显式不做（走 Claude Code 对话）：部署 / 日志 / env / 销毁
 *
 * 详见 docs/modules/internal-app-platform/05-ui-interaction-spec.md
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { toast } from 'sonner';
import {
  Loader2,
  Copy,
  ExternalLink,
  RefreshCw,
  Trash2,
  Plus,
  AlertTriangle,
  ChevronDown,
} from 'lucide-react';

import {
  deriveTokenUiStatus,
  getMyTokenStatus,
  issueToken,
  listMyApps,
  revokeToken,
  type IssueTokenResponse,
  type MyApp,
  type TokenStatusResponse,
  type TokenUiStatus,
} from '@/services/api/internal-app-platform';

const STATUS_BADGE: Record<MyApp['status'], string> = {
  PENDING: 'bg-gray-100 text-gray-700',
  BUILDING: 'bg-blue-100 text-blue-700',
  HEALTHY: 'bg-green-100 text-green-700',
  FAILED: 'bg-red-100 text-red-700',
  // DISABLED = IT-Admin 强停可恢复（暖色/警示），DISABLED_ARCHIVED = 30 天到期归档终态（冷色/归档）
  // 见 docs/modules/internal-app-platform/05-ui-interaction-spec.md badge 配色策略
  DISABLED: 'bg-orange-100 text-orange-700',
  DESTROYED: 'bg-gray-200 text-gray-600',
  DISABLED_ARCHIVED: 'bg-slate-200 text-slate-600',
  PURGED: 'bg-gray-300 text-gray-500',
};

function fmt(s: string, vars: Record<string, string | number>): string {
  return s.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`));
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function fmtDateShort(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}

// HTTP (non-secure context) 下 navigator.clipboard 为 undefined，需要 execCommand 兜底。
// 见 .learnings/2026-05-14-clipboard-http-fallback.md
async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fallthrough to execCommand
    }
  }
  if (typeof document === 'undefined') return false;
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.top = '-9999px';
  ta.style.opacity = '0';
  ta.setAttribute('readonly', '');
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  document.body.removeChild(ta);
  return ok;
}

export default function MyAppsPage() {
  const { t } = useTranslation();
  const tr = t.internalApps;

  const [tokenStatus, setTokenStatus] = useState<TokenStatusResponse | null>(null);
  const [apps, setApps] = useState<MyApp[]>([]);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingApps, setLoadingApps] = useState(true);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [appsError, setAppsError] = useState<string | null>(null);
  const [showDestroyed, setShowDestroyed] = useState(false);
  const [busy, setBusy] = useState(false);
  // 仅"本次生成"内存里短暂持有的明文 + 完整命令；不渲染到 DOM
  const [freshlyIssued, setFreshlyIssued] = useState<IssueTokenResponse | null>(null);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeInput, setRevokeInput] = useState('');
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  // 跟"剪贴板是否真的写成功"绑定的 UI 文案——HTTP 下 clipboard 不可用时不要骗用户
  const [commandCopied, setCommandCopied] = useState(false);

  // ---------- 加载 ----------
  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    setTokenError(null);
    try {
      const s = await getMyTokenStatus();
      setTokenStatus(s);
    } catch {
      setTokenError(tr.errors.loadStatus);
    } finally {
      setLoadingStatus(false);
    }
  }, [tr.errors.loadStatus]);

  const loadApps = useCallback(async (withDestroyed: boolean) => {
    setLoadingApps(true);
    setAppsError(null);
    try {
      const r = await listMyApps(withDestroyed);
      setApps(r.apps);
    } catch {
      setAppsError(tr.errors.loadApps);
    } finally {
      setLoadingApps(false);
    }
  }, [tr.errors.loadApps]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    loadApps(showDestroyed);
  }, [loadApps, showDestroyed]);

  // ---------- token 动作 ----------
  const handleIssue = useCallback(async () => {
    setBusy(true);
    try {
      const r = await issueToken();
      const copied = await copyToClipboard(r.mcpAddCommand);
      setFreshlyIssued(r);
      setCommandCopied(copied);
      setRegenerateOpen(false);
      if (copied) {
        toast.success(tr.onboard.toastIssued);
      } else {
        toast.error(tr.onboard.toastCopyFailed);
      }
      await loadStatus();
    } catch {
      toast.error(tr.onboard.toastError);
    } finally {
      setBusy(false);
    }
  }, [
    loadStatus,
    tr.onboard.toastCopyFailed,
    tr.onboard.toastError,
    tr.onboard.toastIssued,
  ]);

  // ACTIVE/EXPIRING 状态下点 Regenerate 必须先弹强确认（旧 token 会立即失效）；
  // NONE/EXPIRED/REVOKED 状态下没有"会失效的旧 token"，直接 issue 即可。
  const handleRegenerateOpen = useCallback(() => {
    setRegenerateOpen(true);
  }, []);

  const handleCopyCommand = useCallback(async () => {
    if (!freshlyIssued) return;
    const ok = await copyToClipboard(freshlyIssued.mcpAddCommand);
    setCommandCopied(ok);
    if (ok) {
      toast.success(tr.onboard.toastCopied);
    } else {
      toast.error(tr.onboard.toastCopyFailed);
    }
  }, [freshlyIssued, tr.onboard.toastCopied, tr.onboard.toastCopyFailed]);

  const handleCopyDeployPrompt = useCallback(async () => {
    const ok = await copyToClipboard(tr.onboard.deployPromptTemplate);
    if (ok) {
      toast.success(tr.onboard.toastDeployPromptCopied);
    } else {
      toast.error(tr.onboard.toastCopyFailed);
    }
  }, [
    tr.onboard.deployPromptTemplate,
    tr.onboard.toastDeployPromptCopied,
    tr.onboard.toastCopyFailed,
  ]);

  const handleRevokeOpen = useCallback(() => {
    setRevokeInput('');
    setRevokeOpen(true);
  }, []);

  const handleRevokeConfirm = useCallback(async () => {
    const ok = revokeInput.trim().toUpperCase() === 'REVOKE' || revokeInput.trim() === '撤销';
    if (!ok) return;
    setBusy(true);
    try {
      await revokeToken('REVOKE');
      setFreshlyIssued(null);
      setRevokeOpen(false);
      toast.success(tr.onboard.toastRevoked);
      await loadStatus();
    } catch {
      toast.error(tr.onboard.toastError);
    } finally {
      setBusy(false);
    }
  }, [loadStatus, revokeInput, tr.onboard.toastError, tr.onboard.toastRevoked]);

  // ---------- 派生态 ----------
  const uiStatus: TokenUiStatus = tokenStatus
    ? deriveTokenUiStatus(tokenStatus)
    : 'NONE';

  const statusText = (() => {
    if (!tokenStatus) return '';
    switch (uiStatus) {
      case 'NONE':
        return tr.onboard.statusNone;
      case 'ACTIVE':
        return fmt(tr.onboard.statusActive, {
          days: tokenStatus.expiringInDays ?? 0,
          date: fmtDateShort(tokenStatus.expiresAt),
        });
      case 'EXPIRING':
        return fmt(tr.onboard.statusExpiring, {
          days: tokenStatus.expiringInDays ?? 0,
        });
      case 'EXPIRED':
        return fmt(tr.onboard.statusExpired, {
          date: fmtDateShort(tokenStatus.expiresAt),
        });
      case 'REVOKED':
        return fmt(tr.onboard.statusRevoked, {
          date: fmtDate(tokenStatus.lastUsedAt) /* fallback */,
        });
      default:
        return '';
    }
  })();

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">{tr.pageTitle}</h1>
        <p className="text-sm text-gray-500">{tr.pageSubtitle}</p>
      </header>

      {/* ===== 区块 A：接入 Claude Code ===== */}
      <section className="bg-white rounded-lg shadow-sm border p-5 space-y-3">
        <h2 className="text-base font-semibold">{tr.onboard.sectionTitle}</h2>

        {loadingStatus ? (
          <div className="flex items-center text-gray-500 text-sm">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ...
          </div>
        ) : tokenError ? (
          <div className="text-sm text-red-600 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {tokenError}
            <button
              onClick={loadStatus}
              className="ml-2 text-blue-600 hover:underline"
            >
              {tr.errors.retry}
            </button>
          </div>
        ) : (
          <>
            <div
              className={`text-sm font-medium ${
                uiStatus === 'ACTIVE'
                  ? 'text-green-700'
                  : uiStatus === 'EXPIRING'
                    ? 'text-orange-600'
                    : uiStatus === 'EXPIRED' || uiStatus === 'REVOKED'
                      ? 'text-red-600'
                      : 'text-gray-600'
              }`}
            >
              {statusText}
            </div>

            {/* 命令模板（token 段始终用 •••• 占位；点 [复制完整命令] 才把内存里的明文带进剪贴板） */}
            <pre className="bg-gray-50 border rounded p-3 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all">
              {`claude mcp add --transport http ffoa-apps \\
  ${freshlyIssued?.mcpEndpoint ?? 'https://<server>/api/v1/internal-apps/mcp'} \\
  --header "Authorization: Bearer ${
    freshlyIssued
      ? commandCopied
        ? tr.onboard.maskedCopied
        : tr.onboard.maskedCopyHint
      : '••••••••'
  }"`}
            </pre>
            <p className="text-xs text-gray-500">{tr.onboard.copyMaskedHint}</p>

            {/* 按钮组 */}
            <div className="flex flex-wrap gap-2 pt-1">
              {uiStatus === 'NONE' && (
                <button
                  onClick={handleIssue}
                  disabled={busy}
                  className="inline-flex items-center px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {tr.onboard.actions.generate}
                </button>
              )}
              {(uiStatus === 'EXPIRED' || uiStatus === 'REVOKED') && (
                <button
                  onClick={handleIssue}
                  disabled={busy}
                  className="inline-flex items-center px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {tr.onboard.actions.generate}
                </button>
              )}
              {(uiStatus === 'ACTIVE' || uiStatus === 'EXPIRING') && (
                <>
                  {freshlyIssued && (
                    <button
                      onClick={handleCopyCommand}
                      className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-blue-600 text-blue-600 hover:bg-blue-50"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      {tr.onboard.actions.copyCommand}
                    </button>
                  )}
                  <button
                    onClick={handleRegenerateOpen}
                    disabled={busy}
                    className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border hover:bg-gray-50 disabled:opacity-50"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    {tr.onboard.actions.regenerate}
                  </button>
                  <button
                    onClick={handleRevokeOpen}
                    disabled={busy}
                    className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    {tr.onboard.actions.revoke}
                  </button>
                </>
              )}
            </div>

            {/* 状态提示 */}
            {uiStatus === 'EXPIRING' && (
              <p className="text-xs text-orange-600">{tr.onboard.expiringWarning}</p>
            )}
            {uiStatus === 'EXPIRED' && (
              <p className="text-xs text-red-600">{tr.onboard.expiredHint}</p>
            )}
            {uiStatus === 'REVOKED' && (
              <p className="text-xs text-red-600">{tr.onboard.revokedHint}</p>
            )}

            <p className="text-xs text-gray-500">{tr.onboard.instructionHint}</p>
          </>
        )}

        {/* 背景见 .learnings/2026-05-18-claude-ignores-ffoa-apps-mcp-generates-generic-deploy.md */}
        <div className="mt-3 border-t pt-3 space-y-2">
          <h3 className="text-sm font-semibold text-gray-800">
            {tr.onboard.deployPromptTitle}
          </h3>
          <pre className="bg-amber-50 border border-amber-200 rounded p-3 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all text-gray-800">
            {tr.onboard.deployPromptTemplate}
          </pre>
          <button
            onClick={handleCopyDeployPrompt}
            className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-amber-500 text-amber-700 hover:bg-amber-50"
          >
            <Copy className="w-4 h-4 mr-1" />
            {tr.onboard.actions.copyDeployPrompt}
          </button>
          <p className="text-xs text-gray-500">{tr.onboard.deployPromptHint}</p>
        </div>
      </section>

      {/* ===== 区块 B：我的 apps ===== */}
      <section className="bg-white rounded-lg shadow-sm border p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">
            {apps.length > 0
              ? fmt(tr.apps.sectionTitleWithCount, { count: apps.length })
              : tr.apps.sectionTitle}
          </h2>
          <button
            onClick={() => setShowDestroyed((v) => !v)}
            className="text-xs text-blue-600 hover:underline"
          >
            {showDestroyed ? tr.apps.hideDestroyed : tr.apps.showDestroyed}
          </button>
        </div>

        {loadingApps ? (
          <div className="flex items-center text-gray-500 text-sm">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ...
          </div>
        ) : appsError ? (
          <div className="text-sm text-red-600 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {appsError}
            <button
              onClick={() => loadApps(showDestroyed)}
              className="ml-2 text-blue-600 hover:underline"
            >
              {tr.errors.retry}
            </button>
          </div>
        ) : apps.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p className="text-sm">{tr.apps.empty}</p>
            <p className="text-xs mt-1">{tr.apps.emptyHint}</p>
          </div>
        ) : (
          <ul className="divide-y">
            {apps.map((app) => {
              const isHealthy = app.status === 'HEALTHY';
              const isDestroyed =
                app.status === 'DESTROYED' ||
                app.status === 'DISABLED_ARCHIVED' ||
                app.status === 'PURGED';
              return (
                <li key={app.id} className="py-3 space-y-1">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-medium text-gray-900 truncate">
                      {app.appSlug}
                      {app.displayName && (
                        <span className="text-gray-400 font-normal ml-2 text-sm">
                          {app.displayName}
                        </span>
                      )}
                    </div>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_BADGE[app.status]}`}
                    >
                      {isDestroyed && app.retentionUntil
                        ? fmt(tr.apps.destroyedWithRecover, {
                            date: fmtDateShort(app.retentionUntil),
                          })
                        : tr.apps.status[app.status]}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 break-all">
                    {isDestroyed ? (
                      <span className="text-gray-400">{tr.apps.urlOffline}</span>
                    ) : (
                      <a
                        href={app.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {app.url}
                      </a>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>
                      {isDestroyed
                        ? fmt(tr.apps.destroyedAt, { date: fmtDate(app.destroyedAt) })
                        : fmt(tr.apps.lastDeployedAt, { date: fmtDate(app.lastDeployedAt) })}
                    </span>
                    {isHealthy && (
                      <div className="flex gap-2">
                        <button
                          onClick={async () => {
                            const ok = await copyToClipboard(app.url);
                            if (ok) {
                              toast.success(tr.onboard.toastUrlCopied);
                            } else {
                              toast.error(tr.onboard.toastCopyFailed);
                            }
                          }}
                          className="text-blue-600 hover:underline"
                        >
                          {tr.apps.actions.copyUrl}
                        </button>
                        <a
                          href={app.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-blue-600 hover:underline"
                        >
                          {tr.apps.actions.open}
                          <ExternalLink className="w-3 h-3 ml-0.5" />
                        </a>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ===== 区块 C：使用指南（折叠） ===== */}
      <details className="bg-white rounded-lg shadow-sm border group">
        <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
          <div>
            <h2 className="text-base font-semibold">{tr.usageGuide.sectionTitle}</h2>
            <p className="text-xs text-gray-500 mt-1">{tr.usageGuide.sectionHint}</p>
          </div>
          <ChevronDown className="w-5 h-5 text-gray-400 transition-transform group-open:rotate-180" />
        </summary>
        <div className="border-t px-5 py-4 space-y-5 text-sm">
          <ol className="space-y-3">
            {tr.usageGuide.steps.map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold flex items-center justify-center">
                  {i + 1}
                </span>
                <div>
                  <div className="font-medium text-gray-900">{step.title}</div>
                  <p className="text-gray-600 mt-0.5">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>

          <div>
            <h3 className="font-semibold text-gray-900 mb-2">{tr.usageGuide.faqTitle}</h3>
            <dl className="space-y-2">
              {tr.usageGuide.faq.map((item, i) => (
                <div key={i} className="border-l-2 border-gray-200 pl-3">
                  <dt className="font-medium text-gray-800">{item.q}</dt>
                  <dd className="text-gray-600 mt-0.5">{item.a}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-2">{tr.usageGuide.boundariesTitle}</h3>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              {tr.usageGuide.boundaries.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          </div>

          <p className="text-xs text-gray-400 pt-2 border-t">{tr.usageGuide.fullDocLink}</p>
        </div>
      </details>

      {/* ===== 重新生成确认模态 ===== */}
      {regenerateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 m-4 space-y-3">
            <h3 className="text-lg font-semibold text-gray-900">
              {tr.onboard.regenerateConfirmTitle}
            </h3>
            <p className="text-sm text-gray-600 whitespace-pre-line">
              {tr.onboard.regenerateConfirmBody}
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setRegenerateOpen(false)}
                disabled={busy}
                className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                {tr.onboard.regenerateConfirmCancel}
              </button>
              <button
                onClick={handleIssue}
                disabled={busy}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {tr.onboard.regenerateConfirmSubmit}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== 撤销确认模态 ===== */}
      {revokeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 m-4 space-y-3">
            <h3 className="text-lg font-semibold text-red-600">
              {tr.onboard.revokeConfirmTitle}
            </h3>
            <p className="text-sm text-gray-600 whitespace-pre-line">
              {tr.onboard.revokeConfirmBody}
            </p>
            <label className="block text-sm">
              <span className="text-gray-700">{tr.onboard.revokeConfirmInputLabel}</span>
              <input
                type="text"
                value={revokeInput}
                onChange={(e) => setRevokeInput(e.target.value)}
                placeholder={tr.onboard.revokeConfirmPlaceholder}
                autoFocus
                className="mt-1 block w-full rounded border px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-red-500"
              />
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setRevokeOpen(false)}
                className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50"
              >
                {tr.onboard.revokeConfirmCancel}
              </button>
              <button
                onClick={handleRevokeConfirm}
                disabled={
                  busy ||
                  !(
                    revokeInput.trim().toUpperCase() === 'REVOKE' ||
                    revokeInput.trim() === '撤销'
                  )
                }
                className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
              >
                {tr.onboard.revokeConfirmSubmit}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
